import * as Sentry from '@sentry/react';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api, { settingsAPI } from '../lib/api';
import {
  Loader2,
  Eye,
  EyeOff,
  ArrowRight,
  Users,
  FileText,
  BarChart3,
  Shield,
} from 'lucide-react';

const LOGO_URL = "/logo-ldws.png";

// Palette page de garde — alignée sur le site ldwsgroupe.com (fond clair,
// vert de marque #00AD7B). Le dégradé de fond est volontairement très doux.
const BG_GRADIENT = 'linear-gradient(180deg, #f7f5fb 0%, #f8f5f1 50%, #f6f1e9 100%)';
const GREEN = '#00AD7B';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [logoUrl, setLogoUrl] = useState(LOGO_URL);

  // Check if initial setup is needed + load branding
  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        const response = await api.get('/auth/setup-status');
        setNeedsSetup(response.data.needs_setup);
      } catch (error) {
        // ignore
      } finally {
        setCheckingSetup(false);
      }
    };
    const loadBranding = async () => {
      try {
        const res = await settingsAPI.getPublicBranding();
        if (res.data?.logo_url) {
          setLogoUrl(`${process.env.REACT_APP_BACKEND_URL}${res.data.logo_url}`);
        }
      } catch {
        // fallback to default logo
      }
    };
    checkSetupStatus();
    loadBranding();
  }, []);

  // Login form state
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  // Register form state
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'backoffice',
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(loginData.email, loginData.password);
      toast.success('Connexion réussie');
      navigate(u?.role === 'commercial' ? '/commercial/dashboard' : '/dashboard');
    } catch (error) {
      // 401 = identifiants invalides, attendu (mot de passe erroné) — pas un bug.
      if (error?.response?.status !== 401) {
        Sentry.captureException(error);
      }
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(registerData);
      toast.success('Compte créé avec succès');
      navigate(u?.role === 'commercial' ? '/commercial/dashboard' : '/dashboard');
    } catch (error) {
      Sentry.captureException(error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    'h-12 bg-white border-[#e9e6ef] text-[#14121c] placeholder:text-[#9a95a8] ' +
    'focus:border-[#00AD7B] focus:ring-[#00AD7B]/20 transition-all';

  const Logo = ({ className = 'h-14' }) => (
    <img src={logoUrl} alt="LDWS Groupe" className={`${className} w-auto object-contain`} />
  );

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      {/* Fond clair — dégradé doux beige/lavande (aligné sur le site) */}
      <div className="absolute inset-0" style={{ background: BG_GRADIENT }} />

      {/* Panneau gauche — branding & features */}
      <div className="hidden lg:flex lg:w-1/2 relative z-10 flex-col items-start justify-between p-12 xl:p-16">
        <Logo className="h-24 self-start" />

        <div className="space-y-8">
          <div>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#14121c] leading-tight tracking-tight mb-5">
              Gérez votre<br />
              <span style={{ color: GREEN }}>activité</span> en toute simplicité.
            </h2>
            <p className="text-[#5f5b6e] text-lg max-w-md">
              Facturation, contrats, équipements et suivi client réunis dans une seule plateforme.
            </p>
          </div>

          <div className="space-y-3 max-w-md">
            <FeatureItem icon={<Users className="w-5 h-5" />} title="Gestion des clients" description="Centralisez toutes vos informations clients." color="green" />
            <FeatureItem icon={<FileText className="w-5 h-5" />} title="Facturation automatisée" description="Générez et suivez vos factures facilement." color="violet" />
            <FeatureItem icon={<BarChart3 className="w-5 h-5" />} title="Tableaux de bord" description="Visualisez vos KPIs en temps réel." color="green" />
          </div>
        </div>

        <div className="text-[#9a95a8] text-sm">
          © 2026 LDWS Groupe — Tous droits réservés
        </div>
      </div>

      {/* Panneau droit — formulaire */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 relative z-10">
        <div className="w-full max-w-md">
          {/* Logo mobile */}
          <div className="lg:hidden flex justify-center mb-8">
            <Logo className="h-12" />
          </div>

          <div
            className="bg-white rounded-[22px] border border-[#eceaf2] p-8"
            style={{ boxShadow: '0 30px 60px -28px rgba(60,40,110,0.4), 0 2px 8px rgba(20,18,28,0.05)' }}
          >
            {checkingSetup ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: GREEN }} />
              </div>
            ) : needsSetup ? (
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2" style={{ color: GREEN }}>
                    <Shield className="w-5 h-5" />
                    <span className="text-sm font-medium">Configuration initiale</span>
                  </div>
                  <h2 className="text-2xl font-bold text-[#14121c]">Créer le compte administrateur</h2>
                  <p className="text-[#5f5b6e] text-sm mt-1">
                    Bienvenue ! Créez votre compte super administrateur pour commencer.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="register-firstname" className="text-[#443f54] text-sm font-medium">Prénom</Label>
                    <Input
                      id="register-firstname"
                      placeholder="Jean"
                      value={registerData.first_name}
                      onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                      required
                      data-testid="register-firstname"
                      className={fieldClass}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-lastname" className="text-[#443f54] text-sm font-medium">Nom</Label>
                    <Input
                      id="register-lastname"
                      placeholder="Dupont"
                      value={registerData.last_name}
                      onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                      required
                      data-testid="register-lastname"
                      className={fieldClass}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-email" className="text-[#443f54] text-sm font-medium">Email</Label>
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="votre@email.com"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    required
                    data-testid="register-email"
                    className={fieldClass}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-password" className="text-[#443f54] text-sm font-medium">Mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="register-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      data-testid="register-password"
                      className={`${fieldClass} pr-12`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9a95a8] hover:text-[#14121c] transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 mt-2 text-white font-semibold border-0 transition-all duration-300 hover:scale-[1.01] active:scale-[0.99]"
                  style={{ background: `linear-gradient(100deg, ${GREEN}, #0a7a59)`, boxShadow: '0 14px 26px -12px rgba(0,173,123,0.6)' }}
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Création...</>
                  ) : (
                    <>Créer mon compte administrateur<ArrowRight className="w-5 h-5 ml-2" /></>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="mb-6">
                  <h2 className="text-[27px] font-bold text-[#14121c] mb-1.5 tracking-tight">Bienvenue</h2>
                  <p className="text-[#5f5b6e]">Connectez-vous à votre espace de gestion.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-[#443f54] text-sm font-medium">Adresse email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="votre@email.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    disabled={loading}
                    required
                    data-testid="login-email"
                    className={fieldClass}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-[#443f54] text-sm font-medium">Mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className={`${fieldClass} pr-12`}
                      disabled={loading}
                      required
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9a95a8] hover:text-[#14121c] transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 mt-4 text-white font-semibold border-0 transition-all duration-300 hover:scale-[1.01] active:scale-[0.99]"
                  style={{ background: `linear-gradient(100deg, ${GREEN}, #0a7a59)`, boxShadow: '0 14px 26px -12px rgba(0,173,123,0.6)' }}
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Connexion en cours...</>
                  ) : (
                    <>Se connecter<ArrowRight className="w-5 h-5 ml-2" /></>
                  )}
                </Button>

                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-[#5f5b6e] hover:text-[#00AD7B] transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
              </form>
            )}
          </div>

          <p className="lg:hidden text-center text-[#9a95a8] text-xs mt-8">
            © 2026 LDWS Groupe
          </p>
        </div>
      </div>
    </div>
  );
};

// Bloc feature (icône + texte) — carte blanche claire
const FeatureItem = ({ icon, title, description, color }) => {
  const tile = color === 'violet'
    ? 'linear-gradient(145deg,#b79bea,#8b6fd6)'
    : 'linear-gradient(145deg,#2bc496,#00AD7B)';
  return (
    <div className="flex items-center gap-3.5 p-4 rounded-2xl bg-white border border-[#ece7f1] transition-transform duration-200 hover:-translate-y-0.5"
         style={{ boxShadow: '0 12px 30px -20px rgba(60,40,110,0.35)' }}>
      <div className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-white" style={{ background: tile }}>
        {icon}
      </div>
      <div>
        <h3 className="font-bold text-[#14121c] text-[15px]">{title}</h3>
        <p className="text-[#5f5b6e] text-sm mt-0.5">{description}</p>
      </div>
    </div>
  );
};

export default LoginPage;
